// Ecosystem telemetry — the same client every Life Produces Life app uses.
//
// Transpiled verbatim (esbuild, types stripped) from
// ai-website-design/src/lib/telemetry.ts, which is live and recording page
// views into lpl_events today. Kept byte-for-byte so both builders report the
// same event shape and can be compared side by side. Do not hand-edit; if the
// source changes, transpile it again.
//
// Sends to the shared `ecosystem-telemetry` edge function, which accepts an
// event only when its app is registered and active in lpl_app_registry and the
// request comes from an origin that app owns. It stores no IP address and no
// user-agent string, and it honours Do Not Track.
const ENDPOINT = "https://uqhqulrqcygsmmzdzemx.supabase.co/functions/v1/ecosystem-telemetry";
const ANON_KEY = "lpl_anon";
const SESSION_KEY = "lpl_sess";
const SESSION_TS_KEY = "lpl_sess_ts";
const UTM_KEY = "lpl_utm";
const SESSION_IDLE_MS = 30 * 60 * 1e3;
const FLUSH_INTERVAL_MS = 5e3;
const FLUSH_AT_COUNT = 10;
let config = null;
let queue = [];
let timer = null;
let currentUserId = null;
let disabled = false;
const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";
function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto)
    return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : r & 3 | 8).toString(16);
  });
}
function safeGet(store, key) {
  var _a;
  try {
    return (_a = store == null ? void 0 : store.getItem(key)) != null ? _a : null;
  } catch (e) {
    return null;
  }
}
function safeSet(store, key, value) {
  try {
    store == null ? void 0 : store.setItem(key, value);
  } catch (e) {
  }
}
function anonId() {
  const existing = safeGet(window.localStorage, ANON_KEY);
  if (existing)
    return existing;
  const id = uuid();
  safeSet(window.localStorage, ANON_KEY, id);
  return id;
}
function sessionId() {
  var _a;
  const now = Date.now();
  const lastSeen = Number((_a = safeGet(window.sessionStorage, SESSION_TS_KEY)) != null ? _a : 0);
  let id = safeGet(window.sessionStorage, SESSION_KEY);
  if (!id || !lastSeen || now - lastSeen > SESSION_IDLE_MS) {
    id = uuid();
    safeSet(window.sessionStorage, SESSION_KEY, id);
  }
  safeSet(window.sessionStorage, SESSION_TS_KEY, String(now));
  return id;
}
function sessionUtm() {
  const stored = safeGet(window.sessionStorage, UTM_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
    }
  }
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "ref"]) {
    const value = params.get(key);
    if (value)
      utm[key] = value.slice(0, 120);
  }
  if (Object.keys(utm).length > 0)
    safeSet(window.sessionStorage, UTM_KEY, JSON.stringify(utm));
  return utm;
}
function doNotTrackEnabled() {
  var _a, _b;
  const nav = window.navigator;
  const dnt = (_b = (_a = nav.doNotTrack) != null ? _a : window.doNotTrack) != null ? _b : nav.msDoNotTrack;
  return dnt === "1" || dnt === "yes";
}
function send(events) {
  if (!config || events.length === 0)
    return;
  const payload = JSON.stringify({ app: config.app, events });
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      const blob = new Blob([payload], { type: "text/plain;charset=UTF-8" });
      if (navigator.sendBeacon(config.endpoint, blob))
        return;
    } catch (e) {
    }
  }
  void fetch(config.endpoint, {
    method: "POST",
    body: payload,
    headers: { "content-type": "application/json" },
    keepalive: true
    // Telemetry must never take the app down with it.
  }).catch(() => void 0);
}
function flush() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0)
    return;
  const batch = queue;
  queue = [];
  send(batch);
}
function scheduleFlush() {
  if (timer)
    return;
  timer = setTimeout(flush, FLUSH_INTERVAL_MS);
}
function initTelemetry(options) {
  var _a, _b, _c;
  if (!isBrowser() || config)
    return;
  config = {
    app: options.app,
    endpoint: (_a = options.endpoint) != null ? _a : ENDPOINT,
    debug: (_b = options.debug) != null ? _b : false,
    respectDoNotTrack: (_c = options.respectDoNotTrack) != null ? _c : true
  };
  if (config.respectDoNotTrack && doNotTrackEnabled()) {
    disabled = true;
    if (config.debug)
      console.info("[lpl-telemetry] disabled by Do Not Track");
    return;
  }
  const onHide = () => {
    if (document.visibilityState === "hidden")
      flush();
  };
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", flush);
}
function track(name, props = {}) {
  if (!isBrowser() || disabled || !config)
    return;
  const event = {
    name,
    anon_id: anonId(),
    session_id: sessionId(),
    user_id: currentUserId,
    path: window.location.pathname,
    referrer: document.referrer || null,
    utm: sessionUtm(),
    props
  };
  if (config.debug)
    console.info("[lpl-telemetry]", name, props);
  queue.push(event);
  if (queue.length >= FLUSH_AT_COUNT)
    flush();
  else
    scheduleFlush();
}
function identify(userId) {
  currentUserId = userId;
}
function trackPageView(props = {}) {
  track("page_view", props);
}
const funnel = {
  signupStarted: (props) => track("signup_started", props != null ? props : {}),
  signupCompleted: (props) => track("signup_completed", props != null ? props : {}),
  /** The app's first real core action — see lpl_app_registry.activation_event. */
  activated: (props) => track("activated", props != null ? props : {}),
  paymentCompleted: (props) => track("payment_completed", props != null ? props : {})
};
function flushTelemetry() {
  flush();
}
export {
  flushTelemetry,
  funnel,
  identify,
  initTelemetry,
  track,
  trackPageView
};
